import { FC } from "react";
import { ROUTES } from "./routes";
import { ROLE } from "../utils/enums";
import {
  CommentsIcon,
  DashboardIcon,
  DeveloperIcon,
  IIconProps,
  PhoneInTalkIcon,
  HashTagIcon,
  PhoneIphoneIcon,
  WhatsAppIcon,
  QuickMetricsIcon,
  ErrorIcon,
  SumiIcon,
} from "../components/icons";
import { SettingsIcon } from "../components/icons/Settings";

export interface MenuItem {
  text?: string;
  type?: "menu" | "separator" | "spacing";
  icon?: FC<IIconProps>;
  iconText?: string;
  prefix?: string;
  link?: string;
  pin?: "standard" | "toggle";
  pinned?: boolean;
  only_main_org?: boolean;
  admin_only?: boolean;
  staff_only?: boolean;
  has_role?: ROLE;
  subMenus?: MenuItem[];
  callback?: () => void;
}

export const MENUS: MenuItem[] = [
  {
    text: "Dashboard",
    type: "menu",
    icon: DashboardIcon,
    link: ROUTES.DASH.PREFIX,
    subMenus: [],
  },
  {
    text: "Quick Metrics",
    type: "menu",
    icon: QuickMetricsIcon,
    prefix: ROUTES.QUICK_METRICS.PREFIX,
    link: "/quick-metrics/calls",
    subMenus: [
      {
        text: "Calls",
        type: "menu",
        link: "/quick-metrics/calls",
        pin: "toggle",
        pinned: true,
      },
      {
        text: "Messages",
        type: "menu",
        link: "/quick-metrics/messages",
        pin: "toggle",
        pinned: false,
      },
      {
        text: "WhatsApp",
        type: "menu",
        link: "/quick-metrics/whatsapp-leads",
        pin: "toggle",
        pinned: false,
      },
    ],
  },
  {
    text: "Calls",
    type: "menu",
    icon: PhoneInTalkIcon,
    prefix: ROUTES.CALLS.PREFIX,
    link: ROUTES.CALLS.INDEX,
    subMenus: [
      { text: "Calls Received", link: ROUTES.CALLS.RECEIVED },
      { text: "Calls Made", link: ROUTES.CALLS.MADE },
      { text: "Blocked Calls", link: ROUTES.CALLS.BLOCKED },
    ],
  },
  {
    text: "Messages",
    type: "menu",
    icon: CommentsIcon,
    prefix: ROUTES.MESSAGES.PREFIX,
    link: ROUTES.MESSAGES.INDEX,
    subMenus: [
      { text: "Messages Received", link: ROUTES.MESSAGES.RECEIVED },
      { text: "Messages Sent", link: ROUTES.MESSAGES.SENT },
      { text: "Blocked Messages", link: ROUTES.MESSAGES.BLOCKED },
    ],
  },
  {
    text: "Numbers",
    type: "menu",
    icon: HashTagIcon,
    link: ROUTES.NUMBERS.PREFIX,
    subMenus: [],
  },
  {
    // No `admin_only`. The screen is offered to every role, the way every other
    // top-level entry is: whether a user can act on it is the API's answer to
    // give, and it gives it -- `hasAdminAccessToOrg` guards the list as well as
    // the writes, so a non-admin gets a 403 that `useAuthRefusal` turns into
    // "You do not have permission to manage phone users for this organisation."
    // over an empty table. Hiding the entry answered the same question by
    // pretending the feature does not exist, which is a worse answer for the
    // admin of one org who is a plain user of another.
    text: "Phone Users",
    type: "menu",
    icon: PhoneIphoneIcon,
    link: ROUTES.PHONE_USERS.PREFIX,
    subMenus: [],
  },
  {
    text: "WhatsApp",
    type: "menu",
    icon: WhatsAppIcon,
    prefix: ROUTES.WHATSAPP.PREFIX,
    link: ROUTES.WHATSAPP.PREFIX,
    subMenus: [
      {
        text: "Accounts",
        type: "menu",
        link: ROUTES.WHATSAPP.ACCOUNTS.INDEX,
        pinned: false,
      },
      {
        text: "Lead Tracking",
        type: "menu",
        link: ROUTES.WHATSAPP.CHAT_INTROS.INDEX,
        pinned: false,
        subMenus: [
          {
            text: "Chat Intros",
            type: "menu",
            link: ROUTES.WHATSAPP.CHAT_INTROS.INDEX,
            pinned: false,
          },
          {
            text: "Chat Intro Logs",
            type: "menu",
            link: ROUTES.WHATSAPP.CHAT_LOGS.INDEX,
            pinned: false,
          },
        ],
      },
      {
        // No `has_role`. Campaigns is offered to every role, and whether the
        // organisation actually has it is the page's answer to give: it renders
        // "You currently don't have campaigns enabled on your account" over a
        // support address, which is a better answer than an entry that is not
        // there. The API agrees about where the gate belongs --
        // `HasOrgCampaignPermission` tests the ORG's permission groups, not the
        // user's role.
        text: "Campaigns",
        type: "menu",
        link: ROUTES.WHATSAPP.CAMPAIGNS.INDEX,
        pinned: false,
        subMenus: [
          {
            text: "Campaigns",
            type: "menu",
            link: ROUTES.WHATSAPP.CAMPAIGNS.INDEX,
            pinned: false,
          },
          {
            text: "Campaign Contact Lists",
            type: "menu",
            link: ROUTES.WHATSAPP.LISTS.INDEX,
            pinned: false,
          },
          {
            text: "Campaign Opt Outs",
            type: "menu",
            link: ROUTES.WHATSAPP.OPT_OUTS.INDEX,
            pinned: false,
          },
        ],
      },
      // {
      //   //TODO adjust when templates is live
      //   text: 'Templates',
      //   type: 'menu',
      //   link: ROUTES.WHATSAPP.CHAT_INTROS.INDEX,
      //   pinned: false,
      //   subMenus: [
      //     {
      //       //TODO adjust when templates is live
      //       text: 'Message Templates',
      //       type: 'menu',
      //       link: ROUTES.WHATSAPP.CHAT_INTROS.INDEX,
      //       pinned: false,
      //     },
      //     {
      //       //TODO adjust when templates is live
      //       text: 'Chat Intro Templates',
      //       type: 'menu',
      //       link: ROUTES.WHATSAPP.CHAT_LOGS.INDEX,
      //       pinned: false,
      //     },
      //   ]
      // },
      {
        text: "Documentation",
        type: "menu",
        link: ROUTES.WHATSAPP.DOCUMENTATION.INDEX,
        pinned: false,
      },
    ],
  },
  // SD-4167 promotes Sumi from a Settings sub-menu to top level, between
  // WhatsApp and Developer as the design has it. Its routes stay mounted under
  // /settings/sumi -- only the navigation moved.
  //
  // ORDER IS LOAD-BEARING: this entry must stay ABOVE Settings. Sidebar picks
  // the active menu with MENUS.find(pathname.startsWith(prefix)), which takes
  // the FIRST match, and "/settings/sumi" is a sub-path of "/settings". Below
  // Settings, every Sumi page would light up Settings instead.
  {
    text: "Sumi (AI)",
    type: "menu",
    icon: SumiIcon,
    prefix: ROUTES.SETTINGS.SUMI.PREFIX,
    link: ROUTES.SETTINGS.SUMI.INDEX,
    subMenus: [
      { text: "Learn About Sumi", link: ROUTES.SETTINGS.SUMI.LEARN_MORE },
      {
        text: "Call Insights",
        link: ROUTES.SETTINGS.SUMI.CALL_INSIGHTS.INDEX,
      },
      {
        text: "Voice Agents",
        link: ROUTES.SETTINGS.SUMI.VOICE_AGENTS.INDEX,
      },
    ],
  },
  {
    text: "Developer",
    type: "menu",
    icon: DeveloperIcon,
    prefix: ROUTES.DEVELOPER.PREFIX,
    link: ROUTES.DEVELOPER.INDEX,
    subMenus: [
      { text: "Documentation", link: ROUTES.DEVELOPER.DOCUMENTATION },
      { text: "API Tokens", link: ROUTES.DEVELOPER.API_TOKENS.INDEX },

      // {
      //   text: 'Issues & Errors',
      //   pinned: false,
      //   prefix: ROUTES.DEVELOPER.ISSUES.PREFIX,
      //   link: ROUTES.DEVELOPER.ISSUES.INDEX,
      //   subMenus: [
      //     {
      //       text: 'Current Errors',
      //       type: 'menu',
      //       link: ROUTES.DEVELOPER.ISSUES.INDEX,
      //       pinned: false,
      //     },
      //     {
      //       text: 'Error Management',
      //       type: 'menu',
      //       link: ROUTES.DEVELOPER.ERROR_MANAGEMENT.INDEX,
      //       pinned: false,
      //     },
      //   ]
      // },
      { text: "Webhooks", link: ROUTES.DEVELOPER.WEBHOOKS.INDEX },
    ],
  },
  {
    text: "Error Reporting",
    type: "menu",
    icon: ErrorIcon,
    prefix: ROUTES.ERROR_REPORTING.PREFIX,
    link: ROUTES.ERROR_REPORTING.INDEX,
    subMenus: [
      {
        text: "WhatsApp",
        subMenus: [
          {
            text: "Current Errors",
            link: ROUTES.ERROR_REPORTING.CURRENT_ERRORS.INDEX,
          },
          {
            text: "Error Notifications",
            link: ROUTES.ERROR_REPORTING.ERROR_NOTIFICATIONS.INDEX,
          },
        ],
      },
    ],
  },
  {
    text: "Settings",
    type: "menu",
    icon: SettingsIcon,
    iconText: "V 1.1.1.",
    prefix: ROUTES.SETTINGS.PREFIX,
    link: ROUTES.SETTINGS.INDEX,
    subMenus: [
      { text: "Profile", link: ROUTES.SETTINGS.PROFILE },
      { text: "Users", link: ROUTES.SETTINGS.USERS.INDEX, admin_only: true },
      {
        text: "Projects",
        link: ROUTES.SETTINGS.PROJECTS.INDEX,
        admin_only: true,
        only_main_org: true,
      },
      // {
      //   text: 'Billing Management',
      //   subMenus: [
      //     { text: 'Billing Information', link: ROUTES.SETTINGS.BILLING.INDEX },
      //     { text: 'Invoices', link: ROUTES.SETTINGS.INVOICES.INDEX },
      //   ],
      // },
      {
        text: "Spam Control",
        subMenus: [
          {
            text: "Blocked Numbers",
            link: ROUTES.SETTINGS.BLOCKED_NUMBERS.PREFIX,
          },
          /* {
            text: "Blocked Keywords",
            link: ROUTES.SETTINGS.BLOCKED_KEYWORDS.PREFIX,
          }, */
        ],
      },
      { text: "Audio Files", link: ROUTES.SETTINGS.AUDIO_FILES.INDEX },
      { text: "Integrations", link: ROUTES.SETTINGS.INTEGRATIONS.INDEX },
      { text: "Two Factor Authentication", link: ROUTES.SETTINGS.TFA },
      // { text: 'Tags', link: ROUTES.SETTINGS.TAGS },
    ],
  },
];
